import React, { useState, useEffect, useRef } from 'react';
import { Container, Form, Col, Row } from 'react-bootstrap';
import { Navigate, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Formik, Field } from 'formik';
import { useTranslation, Trans } from 'react-i18next';
import Cookies from 'js-cookie';
import * as yup from 'yup';

import FieldWrapper from '../components/form/wrappers/Field';
import CaptchaWrapper from '../components/form/wrappers/Captcha';
import Loader from '../components/common/Loader';
import LoaderButton from '../components/common/LoaderButton';

import { useDebounce } from '../utils/hooks';
import { disAllowPassword } from '../utils';
import { RECAPTCHA_SKIP } from '../utils/appConfig';
import {
  PHONE_REG_EXP,
  COOKIES_OPTIONS,
  PRIVACY_POLICY_URL,
  TERMS_OF_SERVICE_URL,
  SDK_AGREEMENT_URL,
  SIGNUP_STEP_COMPLETED,
} from '../constants';

import { authApi } from '../api';

import { useAuth } from '../components/context';

function Register({ user, app, updateApp, setAlert }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [emailStatus, setEmailStatus] = useState({ pending: false, isValid: false });
  const [emailCheck, setEmailCheck] = useState('');
  const debouncedEmail = useDebounce(emailCheck, 750);
  const [recaptchaToken, setRecaptchaToken] = useState(RECAPTCHA_SKIP);
  const [isValidPassword, setIsValidPassword] = useState(true);
  const [password, setPassword] = useState('');
  const debouncedpassword = useDebounce(password, 750);
  const { from } = location.state || { from: { pathname: '/' } };
  const { onLogin } = useAuth();
  const formRef = useRef(null);
  const emailRef = useRef(null);
  const { t } = useTranslation();

  if (window.location.href.includes('admin.silamoney.com')) {
    return (
      <Container className="main-content-container p-0">
        <h2 className="mb-3">{t('register.adminRestricted.title')}</h2>
        <p>{t('register.adminRestricted.message')}</p>
        <NavLink to="/login">{t('register.adminRestricted.loginLink')}</NavLink>
      </Container>
    );
  }

  const initialValues = {
    first_name: '',
    surname: '',
    company_name: '',
    email: app.invitation ? app.invitation.email : '',
    phone: '',
    password: '',
    confirmPassword: '',
    privacy: '',
  };

  const schema = yup.object().shape({
    first_name: yup
      .string()
      .trim()
      .required(
        t('common:form.errors.required', { field: t('common:form.fields.firstName.label') })
      )
      .max(100, t('common:form.errors.max', { count: 100 })),
    surname: yup
      .string()
      .trim()
      .required(t('common:form.errors.required', { field: t('common:form.fields.lastName.label') }))
      .max(100, t('common:form.errors.max', { count: 100 })),
    company_name: yup
      .string()
      .max(95, t('common:form.errors.max', { count: 95 }))
      .required(
        t('common:form.errors.required', { field: t('register.form.fields.companyName.label') })
      ),
    email: !app.invitation
      ? yup
          .string()
          .max(254, t('common:form.errors.max', { count: 254 }))
          .email(t('common:form.fields.email.error'))
          .required(
            t('common:form.errors.required', { field: t('common:form.fields.email.label') })
          )
          .test(
            'checkEmail',
            t('common:form.errors.exists', { field: t('common:form.fields.email.label') }) ||
              t('common:status.validating'),
            (value) => app.validation.checkEmail.isValid && value
          )
      : null,
    phone: yup
      .string()
      .matches(PHONE_REG_EXP, t('common:form.fields.phone.error'))
      .required(t('common:form.errors.required', { field: t('common:form.fields.phone.label') })),
    password: yup
      .string()
      .min(8, t('common:form.fields.password.minError'))
      .max(100, t('common:form.fields.password.maxError'))
      .test(
        'checkValidPassword',
        (!isValidPassword
          ? t('common:form.fields.password.handle')
          : t('common:form.fields.password.error')) || t('common:status.validating'),
        (value) => isValidPassword && value
      ),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password'), null], t('register.form.fields.confirmPassword.error')),
    privacy: yup
      .boolean()
      .oneOf([true], t('register.form.fields.privacy.error'))
      .required(t('register.form.fields.privacy.error')),
  });

  const checkEmail = async (value) => {
    if (value) {
      setEmailStatus({ ...emailStatus, pending: true });
      updateApp({
        validation: {
          ...app.validation,
          checkEmail: { ...app.validation.checkEmail, email: value },
        },
      });
      try {
        const exists = await authApi.confirmAccountExists(value);
        setEmailStatus({ ...emailStatus, isValid: !exists, pending: false });
        updateApp({
          validation: {
            ...app.validation,
            checkEmail: { ...app.validation.checkEmail, isValid: !exists },
          },
        });
      } catch (error) {
        setEmailStatus({ ...emailStatus, isValid: false, pending: false });
        updateApp({
          validation: {
            ...app.validation,
            checkEmail: { ...app.validation.checkEmail, isValid: false, error: error },
          },
        });
      } finally {
        formRef.current.setFieldTouched('email', true);
      }
    }
  };

  const checkValidPassword = async (value) => {
    if (emailCheck && value) {
      setIsValidPassword(disAllowPassword(value, emailCheck));
    }
  };

  const authenticate = async (values, { setSubmitting }) => {
    try {
      const env = app.invitation?.env || 'sandbox';
      const ctoken = RECAPTCHA_SKIP ? null : values.ctoken;
      const response = await authApi.authenticate({
        handle: values.email,
        password: values.password,
        env,
        via: 'register',
        ctoken,
      });

      if (!response.success) {
        setAlert({ message: response.message, variant: 'warning' });
        setSubmitting(false);
        return;
      }

      if (response.user?.sila_device_id) {
        const currentYear = new Date();
        const nextYear = new Date();
        nextYear.setFullYear(currentYear.getFullYear() + 1);
        Cookies.set('sila_console_device_id', response.user.sila_device_id, {
          ...COOKIES_OPTIONS,
          expires: nextYear,
        });
      }

      onLogin(response);
      if (app.invitation && app.invitation.token) {
        updateApp({ invitation: false });
        if (Cookies.get('sila_console_invitation')) Cookies.remove('sila_console_invitation');
      }
      navigate({ pathname: '/welcome', state: { from: location.pathname } });
    } catch (error) {
      setAlert({ message: error, variant: 'warning' });
      setSubmitting(false);
    }
  };

  const register = async (vals, { setSubmitting }) => {
    let values = { ...vals };
    if (!RECAPTCHA_SKIP) {
      values = { ...vals, ctoken: recaptchaToken };
    }
    try {
      const response = await authApi.register(values);
      updateApp({
        registration: {
          ...app.registration,
          ...values,
          id: response.user.id,
          handle: response.user.handle,
          team_id: response.user.team_id,
          team_name: app.invitation.team_name,
          inProgress: true,
        },
      });
      if (app.invitation) {
        values.handle = response.user.handle;
      }
      authenticate(values, { setSubmitting });
    } catch (error) {
      setAlert({ message: error, variant: 'warning' });
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (debouncedpassword) checkValidPassword(debouncedpassword);
  }, [debouncedpassword]);

  useEffect(() => {
    if (debouncedEmail) checkEmail(debouncedEmail);
  }, [debouncedEmail]);

  if (user) {
    return <Navigate to={from} />;
  }

  return (
    <Container className="register main-content-container p-0">
      {app.invitation && app.invitation.team_name && (
        <h2 className="mb-3">
          {t('register.invitedTitle', { team_name: app.invitation.team_name })}
        </h2>
      )}
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={schema}
        innerRef={formRef}
        onSubmit={(values, { setSubmitting }) => {
          try {
            const { email, privacy, surname, ...newValues } = values;
            if (privacy) newValues.privacy_policy_accept = new Date().toString();
            updateApp({
              registration: { ...app.registration, ...newValues, email, surname, inProgress: true },
            });
            register(
              {
                ...newValues,
                email: values.email,
                surname: values.surname,
                invitation_token: app.invitation ? app.invitation.token : null,
                invitation_env: app.invitation ? app.invitation.env : null,
                signup_step_completed: SIGNUP_STEP_COMPLETED.STEP2,
              },
              { setSubmitting }
            );
          } catch (error) {
            setAlert({ message: error, variant: 'warning' });
          }
        }}
      >
        {({ values, errors, handleSubmit, isSubmitting, isValid, setFieldValue, touched }) => (
          <Form noValidate autoComplete="new-password" onSubmit={handleSubmit}>
            <Row>
              <Col sm="12" md="6">
                <Field
                  floating
                  hideError={values.first_name.length === 0}
                  required
                  autofocus
                  material
                  data-hj-whitelist
                  className="first-name"
                  id="registerForm.first_name"
                  label={t('common:form.fields.firstName.label')}
                  name="first_name"
                  fieldClass="data-hj-whitelist"
                  component={FieldWrapper}
                />
              </Col>
              <Col sm="12" md="6">
                <Field
                  floating
                  hideError={values.surname.length === 0}
                  required
                  autofocus
                  material
                  data-hj-whitelist
                  className="last-name"
                  id="registerForm.surname"
                  label={t('common:form.fields.lastName.label')}
                  name="surname"
                  fieldClass="data-hj-whitelist"
                  component={FieldWrapper}
                />
              </Col>
            </Row>
            <Field
              floating
              hideError={values.company_name.length === 0}
              required
              autofocus
              material
              data-hj-whitelist
              id="registerForm.company_name"
              label={t('register.form.fields.companyName.label')}
              name="company_name"
              fieldClass="data-hj-whitelist hide"
              component={FieldWrapper}
            />
            <Field
              floating
              hideError={values.email.length === 0}
              required
              autofocus
              material
              data-hj-whitelist
              readOnly={!!app.invitation}
              className="email"
              id="registerForm.email"
              label={t('common:form.fields.email.workLabel')}
              name="email"
              inputRef={emailRef}
              handleBlur={(e) => checkEmail(e.target.value)}
              handleChange={(e) => {
                const value = e.target.value.toLowerCase().trim();
                setFieldValue(e.target.name, value);
                setEmailCheck(value);
              }}
              isValid={!errors.email && emailStatus && emailStatus.isValid && !emailStatus.pending}
              component={FieldWrapper}
              fieldClass="data-hj-whitelist"
              append={
                !errors.email && emailStatus && emailStatus.pending ? (
                  <Loader size="sm" className="me-3" />
                ) : undefined
              }
            />
            <Field
              floating
              hideError={values.phone.length === 0}
              required
              autofocus
              material
              data-hj-whitelist
              className="phone"
              id="registerForm.phone"
              label={t('common:form.fields.phone.label')}
              name="phone"
              type="tel"
              fieldClass="data-hj-whitelist"
              component={FieldWrapper}
            />
            <Field
              floating
              hideError={values.password.length === 0}
              required
              autofocus
              material
              showstrength
              id="registerPasswordForm.password"
              label={t('register.form.fields.password.label')}
              name="password"
              type="password"
              handleChange={(e) => setPassword(e.target.value)}
              component={FieldWrapper}
            />
            <Field
              floating
              hideError={values.confirmPassword.length === 0}
              required
              autofocus
              material
              className="confirmPassword"
              id="registerPasswordForm.confirmPassword"
              label={t('register.form.fields.confirmPassword.label')}
              name="confirmPassword"
              type="password"
              component={FieldWrapper}
            />
            <Row className="d-flex align-items-center my-4">
              <Col>
                <Field
                  floating
                  required
                  className="privacy text-mb mb-1"
                  id="registerForm.privacy"
                  name="privacy"
                  type="checkbox"
                  error={errors.privacy}
                  component={FieldWrapper}
                >
                  <Trans i18nKey="register.form.fields.privacy.label">
                    I agree to the
                    <a
                      href={PRIVACY_POLICY_URL}
                      className="text-reset no-underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Privacy Policy
                    </a>
                    , the
                    <a
                      href={TERMS_OF_SERVICE_URL}
                      className="text-reset no-underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Terms of Service
                    </a>
                    and the
                    <a
                      href={SDK_AGREEMENT_URL}
                      className="text-reset no-underline"
                      target="_blank"
                      rel="noreferrer"
                    >
                      SDK License Agreement
                    </a>
                    .
                  </Trans>
                </Field>
              </Col>
            </Row>
            <CaptchaWrapper
              className="form-group my-5"
              handleChange={(value) => setRecaptchaToken(value)}
            />
            <div className="d-block d-md-flex align-items-center">
              <LoaderButton
                className="me-5 d-sm-block text-uppercase"
                variant="primary"
                type="submit"
                loadingLabel={t('common:status.registering')}
                loading={isSubmitting}
                disabled={
                  Object.keys(errors).length ||
                  !isValid ||
                  !recaptchaToken ||
                  !Object.keys(touched).length
                }
              >
                {t('register.form.buttons.submit')}
              </LoaderButton>
              <p className="text-info mt-5 mb-0 mt-md-0 d-flex align-items-center flex-nowrap">
                {t('register.haveAccount')}
                <NavLink to="/login" className="ms-2 no-underline d-flex align-items-center">
                  <span className="lnk">{t('common:buttons.login')}</span>
                  <i className="sila-icon long-popover-arrow-end ms-2" />
                </NavLink>
              </p>
            </div>
          </Form>
        )}
      </Formik>
    </Container>
  );
}

export default Register;
